/**
 * Input Component
 * Reusable input field with consistent styling, validation, and error handling
 */

import React from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

// Both named and default exports — matches the shadcn-style convention
// used by sibling primitives (button.tsx, card.tsx, select.tsx) which all
// use named exports. Consumers (EmailNotificationSection,
// TransactionReview) import `{ Input }`; the default
// stays so any default-import consumer keeps working.
export function Input({
  label,
  error,
  helperText,
  icon,
  fullWidth = false,
  className = '',
  ...props
}: InputProps) {
  const inputId = props.id || `input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`${fullWidth ? 'w-full' : ''}`}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium mb-1"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {label}
          {props.required && <span className="ml-1" style={{ color: 'var(--color-needs-human-text)' }}>*</span>}
        </label>
      )}
      
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" style={{ color: 'var(--color-text-dim)' }}>
            {icon}
          </div>
        )}

        <input
          id={inputId}
          style={{ borderColor: error ? 'var(--color-brand)' : 'var(--color-border)' }}
          className={`
            ${icon ? 'pl-10' : 'pl-4'}
            pr-4 py-2 w-full
            border rounded-lg
            focus:ring-2 focus:outline-none
            transition-all duration-200
            focus:border-transparent
            disabled:cursor-not-allowed
            ${className}
          `}
          {...props}
        />
      </div>

      {error && (
        <p className="mt-1 text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{error}</p>
      )}

      {!error && helperText && (
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>{helperText}</p>
      )}
    </div>
  );
}

export default Input;
